import React, { createContext, useContext } from "react";
import { errorToObject, v2 } from "@prftesp/cle-logger";
import { useMeetingManager } from "amazon-chime-sdk-component-library-react";
import { useEffect, useRef, useState } from "react";
import { MEETING_ACTIONS } from "../../../../utility/constants";
import errorCodes from "../../../../utility/errorCodes";
import { endMeeting } from "../../services/VideoMeetingService";

const CONTACT_STATE = {
	CONNECTED: "connected",
	ENDED: "ended",
	CONNECTING: "connecting",
};

const COMPONENT_NAME = "CCP components/MeetingTab/VideoMeetingEventProvider";

const VideoMeetingEventContext = createContext(null);

const VideoMeetingEventProvider = ({ children }) => {
	const [meetingRequests, setMeetingRequests] = useState(new Set());
	const [controllers, setControllers] = useState(new Map());
	const [activeContactId, setActiveContactId] = useState();
	const [inMeetingContactId, setInMeetingContactId] = useState();
	const meetingManager = useMeetingManager();
	const currentAgent = useRef(null);
	const events = window.connect.core.getEventBus();

	useEffect(() => {
		const closeMeeting = async () => {
			try {
				await endMeeting(meetingManager.meetingId);
				await meetingManager.leave();
			} catch (error) {
				v2.error("Error while ending video meeting.", {
					additionalData: { error: errorToObject(error) },
					componentName: COMPONENT_NAME,
					logCode: errorCodes.VIDEO_MEETING_END_AGENT_ERROR,
				});
			}
		};
		if (meetingManager.meetingId && !inMeetingContactId) {
			closeMeeting();
		}
	}, [inMeetingContactId, meetingManager, activeContactId]);

	// Handle adding or removing contacts.
	useEffect(() => {
		const handleMessage = async (msg) => {
			const {
				data: { Content },
				chatDetails: { contactId },
			} = msg;
			if (Content === MEETING_ACTIONS.CUST_REQUESTED) {
				setMeetingRequests((current) => {
					const newSet = current.add(contactId);
					return new Set(newSet);
				});
			} else if (
				Content === MEETING_ACTIONS.CUST_ENDED ||
				Content === MEETING_ACTIONS.CUST_REJECTED ||
				Content === MEETING_ACTIONS.CUST_MEETING_INTERRUPTED
			) {
				setMeetingRequests((current) => {
					const newSet = current;
					newSet.delete(contactId);
					return new Set(newSet);
				});
				setInMeetingContactId();
			} else if (Content === MEETING_ACTIONS.CUST_ACCEPTED) {
				v2.verbose("Customer accepted & joined meeting", {
					componentName: COMPONENT_NAME,
				});
			}
		};

		const observeCustomerMessages = async (contact) => {
			const controller = await contact.getAgentConnection().getMediaController();
			controller.onMessage(handleMessage);
			return controller;
		};

		// Handle message listeners for all contacts.
		const initiateObservers = () => {
			const newMap = controllers;
			currentAgent.current?.getContacts().map(async (contact) => {
				const contactState = contact.getState().type;
				const contactChannel = contact.getType();
				if (newMap.hasOwnProperty(contact.contactId) && contactState === CONTACT_STATE.ENDED) {
					newMap[contact.contactId].cleanUpOnParticipantDisconnect();
					delete newMap[contact.contactId];
				} else if (
					(contactState === CONTACT_STATE.CONNECTED || contactState === CONTACT_STATE.CONNECTING) &&
					contactChannel === "chat" &&
					!newMap.hasOwnProperty(contact.contactId)
				) {
					const controller = await observeCustomerMessages(contact);
					newMap[contact.contactId] = controller;
				}
				setControllers(newMap);
			});
		};

		const handleEndContact = async (contactId) => {
			if (activeContactId === contactId) {
				setActiveContactId();
			}
			if (inMeetingContactId === contactId) {
				try {
					setInMeetingContactId();
					await meetingManager.leave();
					await endMeeting(contactId);
				} catch (error) {
					v2.error("Failed to end meeting.", {
						additionalData: { error: errorToObject(error) },
						componentName: COMPONENT_NAME,
						logCode: errorCodes.VIDEO_MEETING_END_AGENT_ERROR,
					});
				}
			}
			const newSet = meetingRequests;
			newSet.delete(contactId);
			setMeetingRequests(new Set(newSet));
		};

		const agentSubscription = window.connect.agent(async (agent) => {
			currentAgent.current = agent;
		});

		// listen for accept contact
		const acceptContactSubscription = events.subscribe(window.connect.ContactEvents.ACCEPTED, (event) => {
			if (currentAgent.current && event.contactId) {
				const contact = currentAgent.current.getContacts().find((c) => c.contactId === event.contactId);
				const contactState = contact.getState().type;
				const contactChannel = contact.getType();
				if (
					(contactState === CONTACT_STATE.CONNECTED || contactState === CONTACT_STATE.CONNECTING) &&
					contactChannel === "chat"
				) {
					setActiveContactId(event.contactId);
					initiateObservers();
				}
			}
		});

		// listen for view contact
		const viewContactSubscription = events.subscribe(window.connect.ContactEvents.VIEW, (event) => {
			if (currentAgent.current && event.contactId) {
				const contact = currentAgent.current.getContacts().find((c) => c.contactId === event.contactId);
				if (contact) {
					const contactState = contact.getState().type;
					const contactChannel = contact.getType();
					if (contactState === CONTACT_STATE.CONNECTED && contactChannel === "chat") {
						setActiveContactId(event.contactId);
						initiateObservers();
					}
				}
			}
		});

		// listen for end contact
		const endContactSubscription = events.subscribe(window.connect.ContactEvents.ENDED, async (event) => {
			if (event.contactId) {
				handleEndContact(event.contactId);
				initiateObservers();
			}
		});

		return () => {
			agentSubscription.unsubscribe();
			acceptContactSubscription.unsubscribe();
			viewContactSubscription.unsubscribe();
			endContactSubscription.unsubscribe();
			currentAgent.current = null;
		};
	}, [
		activeContactId,
		inMeetingContactId,
		events,
		setActiveContactId,
		setInMeetingContactId,
		meetingManager,
		meetingRequests,
		setControllers,
		controllers,
	]);

	const removeMeetingRequest = (contactId) => {
		const newSet = meetingRequests;
		newSet.delete(contactId);
		setMeetingRequests(new Set(newSet));
	};

	const sendMessage = async (message) => {
		if (currentAgent.current && activeContactId) {
			const contact = currentAgent.current.getContacts().find((c) => c.contactId === activeContactId);
			const controller = await contact?.getAgentConnection().getMediaController();
			const args = {
				message: message,
				contentType: "text/markdown",
			};
			controller?.sendMessage(args);
		}
	};

	const state = {
		inMeetingContactId,
		activeContactId,
		setInMeetingContactId,
		removeMeetingRequest,
		sendMessage,
		isMeetingRequested: meetingRequests.has(activeContactId),
	};

	return <VideoMeetingEventContext.Provider value={state}>{children}</VideoMeetingEventContext.Provider>;
};

const useVideoMeetingEvents = () => {
	const context = useContext(VideoMeetingEventContext);

	if (!context) {
		throw new Error("useVideoMeetingEvents must be used within VideoMeetingEventProvider");
	}
	return context;
};

export { VideoMeetingEventProvider, useVideoMeetingEvents };
